import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MapIcon, ShieldCheckIcon, BedDoubleIcon, CalculatorIcon, ClipboardCheckIcon, CrosshairIcon } from 'lucide-react';
export function SolutionSection() {
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };
  const features = [{
    icon: <MapIcon className="w-8 h-8 text-gold-400" data-id="element-182" />,
    title: 'Mapa Maestro de las 16 Sedes',
    desc: 'Análisis logístico estadio por estadio (accesos y transporte de última milla).'
  }, {
    icon: <ShieldCheckIcon className="w-8 h-8 text-gold-400" data-id="element-183" />,
    title: 'El Protocolo Check-In Express',
    desc: 'La hoja de ruta exacta para trámites de Visa, ESTA y eTA sin errores migratorios.'
  }, {
    icon: <BedDoubleIcon className="w-8 h-8 text-gold-400" data-id="element-184" />,
    title: 'Hacks de Alojamiento "No-Hotel"',
    desc: 'Cómo encontrar dónde dormir cerca de los estadios sin pagar precios VIP.'
  }, {
    icon: <CalculatorIcon className="w-8 h-8 text-gold-400" data-id="element-185" />,
    title: 'Calculadora de Presupuesto Real',
    desc: 'Desglose de costos diarios en USA, México y Canadá (comida, SIM cards y transporte).'
  }, {
    icon: <ClipboardCheckIcon className="w-8 h-8 text-gold-400" data-id="element-186" />,
    title: 'Checklist de Documentación Mundialista',
    desc: 'Una lista de verificación para que no olvides ni un solo permiso antes de subir al avión.'
  }];
  return <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-187">
      {/* Background elements */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gold-500/5 rounded-full blur-[100px] pointer-events-none" data-id="element-188" />
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-pitch-500/5 rounded-full blur-[100px] pointer-events-none" data-id="element-189" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-190">
        {/* Mechanism Header */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.7
      }} className="text-center max-w-4xl mx-auto mb-20" data-id="element-191">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold-500/10 text-gold-400 mb-6 border border-gold-500/20" data-id="element-192">
            <CrosshairIcon className="w-8 h-8" data-id="element-193" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 uppercase" data-id="element-194">
            El Navegador{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-gold-600" data-id="element-195">
              Trifrontera
            </span>
          </h2>
          <p className="text-xl text-slate-300 leading-relaxed" data-id="element-196">
            Este no es un libro de lectura, es una herramienta operativa. Hemos
            dividido el continente en{' '}
            <strong className="text-white" data-id="element-197">"Clústeres de Eficiencia"</strong>{' '}
            (Oeste, Central y Este) para que te muevas con la precisión de un
            local, hackeando los cruces fronterizos y utilizando nodos de
            transporte que el turista promedio ignora por completo.
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="mt-16" data-id="element-198">
          <motion.h3 initial={{
          opacity: 0
        }} whileInView={{
          opacity: 1
        }} viewport={{
          once: true
        }} className="text-3xl font-bold text-center text-white mb-12 uppercase tracking-wide" data-id="element-199">
            ¿Qué Incluye Tu Guía?
          </motion.h3>

          <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-50px'
        }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" data-id="element-200">
            {features.map((feature, index) => <motion.div key={index} variants={itemVariants} className="bg-navy-800/80 backdrop-blur-sm border border-navy-700 rounded-2xl p-8 hover:border-gold-500/30 transition-colors duration-300" data-id="element-201">
                <div className="mb-6 bg-navy-900 w-16 h-16 rounded-xl flex items-center justify-center border border-navy-700 shadow-inner" data-id="element-202">
                  {feature.icon}
                </div>
                <h4 className="text-xl font-bold text-white mb-3 font-heading uppercase tracking-wide" data-id="element-203">
                  {feature.title}
                </h4>
                <p className="text-slate-400 leading-relaxed" data-id="element-204">{feature.desc}</p>
              </motion.div>)}

            {/* Filler card to make grid look nice (3x2 = 6 cards, we have 5 features) */}
            <motion.div variants={itemVariants} className="bg-gradient-to-br from-navy-800 to-navy-900 border border-gold-500/20 rounded-2xl p-8 flex flex-col items-center justify-center text-center relative overflow-hidden group" data-id="element-205">
              <div className="absolute inset-0 bg-gold-500/5 group-hover:bg-gold-500/10 transition-colors duration-500" data-id="element-206" />
              <div className="relative z-10" data-id="element-207">
                <h4 className="text-2xl font-bold text-gold-400 mb-2 font-heading uppercase" data-id="element-208">
                  Acceso Inmediato
                </h4>
                <p className="text-slate-300" data-id="element-209">
                  Descarga digital en PDF compatible con todos tus dispositivos.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}